import { create, type StoreApi, type UseBoundStore } from "zustand";
import { listMealPlans } from "wasp/client/operations";
import { useUser } from "../hooks/useUser";
import React, { useEffect } from "react";

export interface MealPlan {
  id: string;
  title: string;
  weekStartDate: string;
  planData?: any;
  [key: string]: any;
}

interface MealPlansState {
  plans: MealPlan[] | null;
  isLoading: boolean;
  lastRefresh: number;
  set: (plans: MealPlan[]) => void;
  addPlan: (plan: MealPlan) => void;
  removePlan: (planId: string) => void;
  updatePlan: (planId: string, updates: Partial<MealPlan>) => void;
  refresh: (isAuthenticated?: boolean) => Promise<void>;
  triggerRefresh: () => void;
}

const createMealPlansState = (set: any, get: any): MealPlansState => ({
  plans: null,
  isLoading: false,
  lastRefresh: Date.now(),

  set: (plans: MealPlan[]) => set({ plans }),

  addPlan: (plan: MealPlan) => {
    const { plans } = get();
    if (plans) {
      // Add to the beginning of the list (most recent first)
      set({ plans: [plan, ...plans] });
    }
  },

  removePlan: (planId: string) => {
    const { plans } = get();
    if (plans) {
      set({ plans: plans.filter((p) => p.id !== planId) });
    }
  },

  updatePlan: (planId: string, updates: Partial<MealPlan>) => {
    const { plans } = get();
    if (plans) {
      set({
        plans: plans.map((p) => (p.id === planId ? { ...p, ...updates } : p)),
      });
    }
  },

  refresh: async (isAuthenticated?: boolean) => {
    // Gate: Don't make API calls if not authenticated
    if (isAuthenticated === false) {
      console.debug("MealPlansStore: Skipping refresh - not authenticated");
      set({ plans: null, isLoading: false });
      return;
    }

    set({ isLoading: true });
    try {
      try {
        const plans = (await listMealPlans({})) as any as MealPlan[];
        set({ plans, isLoading: false, lastRefresh: Date.now() });
      } catch (error: any) {
        // Don't spam console with 401s when logged out
        if (error?.status === 401 || error?.statusCode === 401) {
          console.debug("MealPlansStore: 401 - user not authenticated");
          set({ plans: null, isLoading: false });
        } else {
          console.warn("Failed to fetch meal plans");
          console.error(error);
          set({ isLoading: false });
        }
      }
    } catch (error) {
      console.error("Error fetching meal plans:", error);
      set({ isLoading: false });
    }
  },

  triggerRefresh: () => {
    set({ lastRefresh: Date.now() });
  },
});

// Export the hook that accepts selectors
export const useMealPlans: UseBoundStore<StoreApi<MealPlansState>> =
  create<MealPlansState>()(createMealPlansState);

// Typed selectors
export const selectPlans = (s: MealPlansState) => s.plans;
export const selectMealPlansLoading = (s: MealPlansState) => s.isLoading;
export const selectLastRefresh = (s: MealPlansState) => s.lastRefresh;

// Helper hook for optimistic updates
export const useOptimisticMealPlanUpdate = () => {
  const { addPlan, updatePlan, removePlan, triggerRefresh } = useMealPlans();

  return {
    createPlan: async (
      optimisticPlan: MealPlan,
      apiCall: () => Promise<MealPlan>,
    ) => {
      // Optimistic add
      addPlan(optimisticPlan);

      try {
        // Make the API call
        const actualPlan = await apiCall();
        // Update with real data
        updatePlan(optimisticPlan.id, actualPlan);
        triggerRefresh();
      } catch (error) {
        // On error, remove the optimistic plan
        console.error("Plan creation failed:", error);
        removePlan(optimisticPlan.id);
        throw error;
      }
    },

    deletePlan: async (planId: string, apiCall: () => Promise<void>) => {
      const { plans } = useMealPlans.getState();
      const planToRemove = plans?.find((p) => p.id === planId);

      // Optimistic remove
      removePlan(planId);

      try {
        // Make the API call
        await apiCall();
        triggerRefresh();
      } catch (error) {
        // On error, restore the plan
        console.error("Plan deletion failed:", error);
        if (planToRemove) {
          addPlan(planToRemove);
        }
        throw error;
      }
    },
  };
};

// Hook to connect auth state with store
export const useAuthenticatedMealPlans: UseBoundStore<
  StoreApi<MealPlansState>
> = useMealPlans;

// Hook that auto-refreshes based on auth state
export const useAuthenticatedMealPlansWithRefresh = () => {
  const { isAuthenticated, isLoading: authLoading } = useUser();
  const store = useMealPlans();

  // Auto-refresh when auth state changes
  React.useEffect(() => {
    if (!authLoading) {
      store.refresh(isAuthenticated);
    }
  }, [isAuthenticated, authLoading]);

  return store;
};

// Auto-refresh on page focus (only if authenticated)
if (typeof window !== "undefined") {
  let isAuth = false;

  const handleVisibilityChange = () => {
    if (!document.hidden && isAuth) {
      useMealPlans.getState().refresh(true);
    }
  };

  // Track auth state for visibility handler
  const updateAuthState = () => {
    // This will be updated by the hook above
    const state = useMealPlans.getState();
    isAuth = state.plans !== null; // Simple heuristic
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  setInterval(updateAuthState, 1000); // Check auth state periodically
}
